import { and } from '@ember/object/computed';
import { computed } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import { inject as service } from '@ember/service';

import BaseAnalytics from './base-analytics';

// parameters
const DATE = 'completedAt';
const COST_CODE = 'costCode';
const STORY_CODE = 'storyCode';
const COST = 'totalInvoice';
const FREELANCER_EARNINGS = 'totalPayment';
const MONTH = 'MONTH';

// factoids
const FACTOID_SPEND_YEAR = 'Spend YTD';
const FACTOID_SPEND_MONTH  = 'Avg. Monthly Spend YTD';
const FACTOID_SPEND_PROJECT = 'Avg. Project Spend YTD';
const FACTOID_PROJECT_COMPLETE = 'Projects Completed YTD';
const FACTOID_PROJECT_MONTH = 'Avg. Projects Monthly';
const FACTOID_PROJECT_LENGTH = 'Avg. Project Length';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];


export default BaseAnalytics.extend({
  session: service(),
  store: service(),
  currentUser: readOnly('session.currentUser'),

  ////////////
  // CONFIG //
  ////////////
  title: computed('year', function() {
    const year = this.get('year');
    if (!year) {
      return 'Completed Projects';
    }
    const dateString = (year === new Date().getFullYear()) ? 'YTD' : year;

    return `Completed Projects - ${dateString}`;
  }),

  factoidType: 'spend',
  annual: true,
  hideGraph: false,
  allowTableDownload: true,
  defaultFileName: computed('year', function() {
    const year = this.get('year');
    return `Glimmer_Projects_Report_${year}.csv`;
  }),

  ////////////////
  // FORMATTING //
  ////////////////
  data: computed({
    set(_key, data) {
      return data.map(project => {
        const month = project.get(DATE).getMonth();

        project.set(MONTH, month);

        return project;
      });
    }
  }),

  ///////////
  // CHART //
  ///////////
  chartKeyParam: MONTH,

  chartSumParams: [
    {
      label: 'By Spend',
      value: COST,
      defaultParam: true,
    },
    {
      label: 'By Count',
      value: null,
    }
  ],

  chartGroupParams: and('usesCostCodes', '_chartGroupParams'),

  _chartGroupParams: [
    {
      label: 'By Cost Codes',
      value: COST_CODE,
      defaultParam: true,
    },
    {
      label: 'By Story Codes',
      value: STORY_CODE
    },
  ],

  chartLabels: computed('year', function() {
    const currentYear = new Date().getFullYear();
    const year = this.get('year');
    // for the current year we only want to show up to the current month
    const numMonths = (year === currentYear) ? new Date().getMonth() + 1 : 12;

    const labels = {};
    for (let month = 0; month < numMonths; month++) {
      labels[month] = MONTHS[month];
    }

    return labels;
  }),

  chartSumFormatter: computed('sumParam', function() {
    const sumParam = this.get('sumParam');
    switch (sumParam) {
      case COST:
        return this.formatMoneyWithoutChange.bind(this);
      default:
        return null;
    }
  }),

  ///////////
  // TABLE //
  ///////////
  tableColumns: computed('usesCostCodes', function() {
    const usesCostCodes = this.get('usesCostCodes');

    const formatMoney = this.formatMoney.bind(this);
    const formatDate = this.formatDate.bind(this);
    let columns = [
      {
        label: 'Completion',
        csvLabel: 'Project Completion Date',
        param: DATE,
        sortDescending: true,
        defaultSort: true,
        display: formatDate
      },
      {
        label: 'Project',
        csvLabel: 'Project Title',
        param: 'title',
        wide: true,
        link: (value, model) => `/project/${model.get('id')}`
      },
      {
        label: 'Cost Codes',
        param: COST_CODE,
      },
      {
        label: 'Story Codes',
        param: STORY_CODE,
      },
      {
        label: 'Hired By',
        csvLabel: 'Editor',
        param: 'buyer.name',
        link: (value, model) => `/${model.get('buyer.slug')}`
      },
      {
        label: 'Creator',
        csvLabel: 'Creator Name',
        param: 'seller.name',
        link: (value, model) => `/${model.get('seller.slug')}`
      },
      {
        label: 'Spend ($USD)',
        csvLabel: 'Project Spend',
        param: COST,
        sortDescending: true,
        display: formatMoney
      },
      {
        csvLabel: 'Creator Earnings',
        param: FREELANCER_EARNINGS,
        hidden: true,
        display: formatMoney
      }
    ];

    if (!usesCostCodes) {
      columns = columns.filter(column => {
        return (column.param !== COST_CODE && column.param !== STORY_CODE);
      });
    }

    return columns;
  }),

  //////////////
  // FACTOIDS //
  //////////////
  factoidLayout: computed('media.isSearchMobile', function() {
    const mobile = this.get('media.isSearchMobile');
    if (mobile) {
      return [
        [{title: FACTOID_SPEND_YEAR}, {title: FACTOID_SPEND_MONTH}],
        [{title: FACTOID_SPEND_PROJECT}, {title: FACTOID_PROJECT_COMPLETE}],
        [{title: FACTOID_PROJECT_MONTH}, {title: FACTOID_PROJECT_LENGTH}]
      ];
    }

    return [
      [{title: FACTOID_SPEND_YEAR}, {title: FACTOID_SPEND_MONTH}, {title: FACTOID_SPEND_PROJECT}],
      [{title: FACTOID_PROJECT_COMPLETE}, {title: FACTOID_PROJECT_MONTH}, {title: FACTOID_PROJECT_LENGTH}]
    ];
  }),

  /////////////
  // METHODS //
  /////////////
  queryData(year) {
    const companyId = this.get('currentUser.company.id');
    return this.get('store').query('project-metric', {
      start_at: new Date(year, 0, 1).toISOString(),
      end_at: new Date(year + 1, 0, 1).toISOString(),
      filter: {
        company_id: companyId
      }
    });
  },

  peekData(year) {
    return this.get('store').peekAll('project-metric').filter(item => {
      return item.get(DATE).getFullYear() === year;
    });
  },
});
